import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import PropTypes from 'prop-types';
import Stack from '@mui/material/Stack';

// Import our component files
import HighlightCard from './components/HighlightCard';
import GroupCard from './components/GroupCard';
import ExchangeRateCard from './components/ExchangeRateCard';
import SummaryCard from './components/SummaryCard';
import ExportPDFButton from '../../components/ExportPDFButton';
import { useOrderType } from '../../context/OrderTypeContext';

/**
 * SummaryHeader - Displays the summary header with key financial information
 * Main component that renders financial summary information with responsive layout
 *
 * @param {Object} props - Component props
 * @param {Object} props.taxCalculation - Tax calculation data
 * @param {Object} props.order - Order data for PDF export
 */
const SummaryHeader = ({ taxCalculation, order }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { isEncomenda } = useOrderType();

  if (!taxCalculation) return null;

  // Component to render based on screen size
  const renderSummaryCards = () => {
    if (isMobile) {
      return (
        <>
          {/* Highlight Card - Always visible and prominent */}
          <HighlightCard
            title="Final Import Cost"
            value={taxCalculation.custo_total_brl}
          />

          {/* Main Values Group */}
          <GroupCard
            title="Main Values"
            metrics={[
              { title: 'Total Customs Value', value: taxCalculation.valor_aduaneiro_brl },
              { title: 'Total Customs Taxes', value: taxCalculation.tributos_alfandegarios_brl },
              { title: 'Total Invoice Value', value: taxCalculation.nota_fiscal_brl },
            ]}
          />

          {/* Financial Details Group */}
          <GroupCard
            title="Financial Details"
            metrics={[
              { title: 'Total Tax Credits', value: taxCalculation.total_tax_credits_brl },
              { title: 'Total Disbursement', value: taxCalculation.desembolso_total_brl, isHighlighted: true },
            ]}
          />

          {/* Encomenda-specific cards */}
          {isEncomenda && (
            <GroupCard
              title="Encomenda"
              metrics={[
                { title: 'Custo AVX', value: taxCalculation.custo_avx_brl },
                { title: 'Gross Operating Result', value: taxCalculation.gross_operating_result_brl },
              ]}
            />
          )}
        </>
      );
    }

    // Desktop view - grid layout
    return (
      <>
        {/* Summary Cards - First Row */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={4} md={4}>
            <SummaryCard
              title="Total Customs Value"
              value={taxCalculation.valor_aduaneiro_brl}
            />
          </Grid>
          <Grid item xs={12} sm={4} md={4}>
            <SummaryCard
              title="Total Customs Taxes"
              value={taxCalculation.tributos_alfandegarios_brl}
            />
          </Grid>
          <Grid item xs={12} sm={4} md={4}>
            <SummaryCard
              title="Total Invoice Value"
              value={taxCalculation.nota_fiscal_brl}
            />
          </Grid>
        </Grid>

        {/* Summary Cards - Second Row */}
        <Grid container spacing={2} sx={{ mb: isEncomenda ? 2 : 0 }}>
          <Grid item xs={12} sm={4} md={4}>
            <SummaryCard
              title="Total Tax Credits"
              value={taxCalculation.total_tax_credits_brl}
            />
          </Grid>
          <Grid item xs={12} sm={4} md={4}>
            <SummaryCard
              title="Total Disbursement"
              value={taxCalculation.desembolso_total_brl}
              isHighlighted={true}
            />
          </Grid>
          <Grid item xs={12} sm={4} md={4}>
            <SummaryCard
              title="Final Import Cost"
              value={taxCalculation.custo_total_brl}
              isHighlighted={true}
            />
          </Grid>
        </Grid>

        {/* Encomenda-specific cards - Third Row */}
        {isEncomenda && (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4} md={4}>
              <SummaryCard
                title="Custo AVX"
                value={taxCalculation.custo_avx_brl}
              />
            </Grid>
            <Grid item xs={12} sm={4} md={4}>
              <SummaryCard
                title="Gross Operating Result"
                value={taxCalculation.gross_operating_result_brl}
              />
            </Grid>
          </Grid>
        )}
      </>
    );
  };

  return (
    <Box mb={4}>
      <Stack 
        direction={{ xs: 'column', sm: 'row' }} 
        justifyContent="space-between" 
        alignItems={{ xs: 'stretch', sm: 'center' }}
        spacing={2}
        mb={2}
      >
        <Typography variant="h5">
          Import Costs and Taxes
        </Typography>
        <Box className="no-print">
          <ExportPDFButton order={order} />
        </Box>
      </Stack>

      {/* Exchange Rate Card */}
      <ExchangeRateCard exchangeRate={taxCalculation.exchange_rate_dollar_to_real} />

      {/* Render cards based on screen size */}
      {renderSummaryCards()}
    </Box>
  );
};

SummaryHeader.propTypes = {
  taxCalculation: PropTypes.shape({
    custo_total_brl: PropTypes.number,
    valor_aduaneiro_brl: PropTypes.number,
    tributos_alfandegarios_brl: PropTypes.number,
    nota_fiscal_brl: PropTypes.number,
    total_tax_credits_brl: PropTypes.number,
    desembolso_total_brl: PropTypes.number,
    exchange_rate_dollar_to_real: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
  }),
  order: PropTypes.object
};

export default SummaryHeader;